import { useEffect, useState } from "react";
import { NAVY } from "../styles/palette";
import { getAuthToken, SUPABASE_ANON_KEY, SUPABASE_URL } from "../lib/supabase";
import { GOOGLE_MAPS_KEY } from "../lib/maps";
import { getPrice } from "../lib/pricing";
import { TechJobList } from "./TechJobList";
import { fetchTechVisits, summarizeTechVisits } from "../lib/visitTimes";
import { useIsMobile } from "../lib/useIsMobile";

// Rows shown before a list collapses behind a "show all" toggle.
const LIST_PAGE = 10;

export function TechProfilesPage({ technicians, clients, invoices, pricing, dbVehicles, dbTrips, showToast, onRefresh, onRefreshVehicles }) {
  const isMobile = useIsMobile();
  const [selectedTech, setSelectedTech] = useState(null);
  const [visits,       setVisits]       = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [gpsVisits,    setGpsVisits]    = useState([]);   // GPS auto-tracked time_on_site rows for this tech
  const [showAllVisits,setShowAllVisits]= useState(false);
  // Long tables bury the signal. Each list shows the first PAGE_SIZE rows and
  // expands on demand, so a tech with 200 visits doesn't push everything else
  // off the screen. Reset per tech so switching profiles starts collapsed.
  const [showAllClients,setShowAllClients]= useState(false);
  const [showAllHistory,setShowAllHistory]= useState(false);

  useEffect(()=>{
    if (selectedTech) { loadVisits(selectedTech.id); fetchTechVisits(selectedTech.id).then(setGpsVisits); }
    else { setGpsVisits([]); setShowAllVisits(false); }
    setShowAllClients(false); setShowAllHistory(false);
  },[selectedTech]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadVisits(techId) {
    setLoading(true);
    const res = await fetch(`${SUPABASE_URL}/rest/v1/jobs?technician_id=eq.${techId}&order=scheduled_date.desc&limit=200`,{
      headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}
    }).then(r=>r.json());
    if (Array.isArray(res)) setVisits(res);
    setLoading(false);
  }

  function getTechStats(tech) {
    const assigned   = clients.filter(c=>c.technician_id===tech.id&&c.active).length;
    const techVisits = visits.filter(v=>v.technician_id===tech.id);
    const completed  = techVisits.filter(v=>v.status==="completed").length;
    const skipped    = techVisits.filter(v=>v.status==="skipped").length;
    const total      = completed + skipped;
    const compRate   = total>0 ? Math.round((completed/total)*100) : 0;
    const durations  = techVisits.filter(v=>v.started_at&&v.completed_at).map(v=>Math.floor((new Date(v.completed_at)-new Date(v.started_at))/60000));
    const avgDur     = durations.length>0 ? Math.round(durations.reduce((a,b)=>a+b,0)/durations.length) : 0;
    const revenue    = clients.filter(c=>c.technician_id===tech.id&&c.active&&c.frequency&&c.dogs).reduce((s,c)=>s+getPrice(c.frequency,c.dogs,pricing),0);
    return { assigned, completed, skipped, total, compRate, avgDur, revenue };
  }

  if (selectedTech) {
    const stats = getTechStats(selectedTech);
    const techClients = clients.filter(c=>c.technician_id===selectedTech.id);
    const techVisits  = visits.filter(v=>v.technician_id===selectedTech.id);
    const gps         = summarizeTechVisits(gpsVisits, clients); // GPS clean-time analytics

    return (
      <div>
        <button className="btn btn-ghost btn-sm" onClick={()=>{setSelectedTech(null);setVisits([]);}} style={{marginBottom:16}}>← Back to Technicians</button>

        {/* Tech header */}
        <div className="card" style={{marginBottom:16}}>
          {/* Phone: email and phone on their own lines (no mid-word breaks in the address) and
              the status pill drops under the name instead of squeezing the text column. */}
          <div style={{display:"flex",alignItems:"center",gap:isMobile?12:16,flexWrap:"wrap"}}>
            <div style={{width:56,height:56,borderRadius:"50%",background:NAVY,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,color:"white",fontWeight:700,flexShrink:0}}>
              {selectedTech.name.charAt(0)}
            </div>
            <div style={{flex:"1 1 0",minWidth:0,overflowWrap:"anywhere"}}>
              <div style={{fontWeight:800,fontSize:20,color:"#1a1f36"}}>{selectedTech.name}</div>
              <div style={{fontSize:13,color:"#42526e"}}>
                <span style={{display:isMobile?"block":"inline"}}>{selectedTech.email||"No email"}</span>
                {!isMobile&&" · "}
                <span style={{display:isMobile?"block":"inline"}}>{selectedTech.phone||"No phone"}</span>
              </div>
              <div style={{fontSize:12,color:"#8993a4",marginTop:2}}>${selectedTech.hourly_rate||0}/hr · Default {selectedTech.default_hours||0} hrs/period</div>
            </div>
            <div style={{marginLeft:isMobile?0:"auto",flexBasis:isMobile?"100%":"auto"}}>
              <span className={`badge ${selectedTech.active?"badge-green":"badge-gray"}`}>{selectedTech.active?"Active":"Inactive"}</span>
            </div>
          </div>
        </div>

        {/* Vehicle Assignment */}
        <div className="card" style={{marginBottom:16}}>
          <div className="card-title" style={{marginBottom:10}}>🚗 Assigned Vehicle</div>
          <select
            value={dbVehicles?.find(v=>v.technician_id===selectedTech.id)?.id||""}
            onChange={async e=>{
              const newVehicleId = e.target.value;
              const current = dbVehicles?.find(v=>v.technician_id===selectedTech.id);
              if (current) await fetch(`${SUPABASE_URL}/rest/v1/vehicles?id=eq.${current.id}`,{method:"PATCH",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({technician_id:null,technician_name:null})});
              if (newVehicleId) {
                await fetch(`${SUPABASE_URL}/rest/v1/vehicles?id=eq.${newVehicleId}`,{method:"PATCH",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({technician_id:selectedTech.id,technician_name:selectedTech.name})});
                const veh = dbVehicles?.find(v=>v.id===newVehicleId);
                if (veh) await fetch(`${SUPABASE_URL}/rest/v1/trips?imei=eq.${veh.imei}&override=eq.false`,{method:"PATCH",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({technician_id:selectedTech.id,technician_name:selectedTech.name})});
              }
              onRefreshVehicles&&onRefreshVehicles();
              showToast("Vehicle assignment updated");
            }}
            style={{width:"100%",border:"1px solid #e3e8ef",borderRadius:6,padding:"8px 10px",fontSize:13,background:"white"}}>
            <option value="">— No Vehicle Assigned —</option>
            {(dbVehicles||[]).map(v=>(
              <option key={v.id} value={v.id} disabled={v.technician_id&&v.technician_id!==selectedTech.id}>
                {v.nickname||v.model} — {v.year} {v.make} {v.technician_id&&v.technician_id!==selectedTech.id?`(assigned to ${v.technician_name})`:v.technician_id===selectedTech.id?" ✓ Currently assigned":""}
              </option>
            ))}
          </select>
          {dbVehicles?.find(v=>v.technician_id===selectedTech.id)&&(
            <div style={{fontSize:12,color:"#065F46",marginTop:8}}>
              ✓ Assigned: {(()=>{const v=dbVehicles.find(v=>v.technician_id===selectedTech.id); return `${v.nickname||v.model} (${v.year} ${v.make} · IMEI: ${v.imei})`;})()}
            </div>
          )}
        </div>

        {/* Home Address */}
        <div className="card" style={{marginBottom:16}}>
          <div className="card-title" style={{marginBottom:10}}>🏠 Home Address</div>
          <div style={{fontSize:12,color:"#8993a4",marginBottom:10}}>Used to exclude home location from business trip matching and for future route optimization.</div>
          <input type="text" placeholder="Street address"
            defaultValue={selectedTech.home_address||""}
            style={{width:"100%",border:"1px solid #e3e8ef",borderRadius:6,padding:"8px 10px",fontSize:13,marginBottom:8,boxSizing:"border-box"}}
            onBlur={async e=>{
              const addr = e.target.value.trim();
              // Geocode the address
              if (addr) {
                try {
                  const geo = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(addr)}&key=${GOOGLE_MAPS_KEY}`).then(r=>r.json());
                  const loc = geo.results?.[0]?.geometry?.location;
                  await fetch(`${SUPABASE_URL}/rest/v1/technicians?id=eq.${selectedTech.id}`,{method:"PATCH",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({home_address:addr,home_lat:loc?.lat||null,home_lng:loc?.lng||null})});
                  onRefresh&&onRefresh();
                  showToast(loc?"Home address saved and geocoded":"Home address saved (geocoding failed)");
                } catch(e) {
                  await fetch(`${SUPABASE_URL}/rest/v1/technicians?id=eq.${selectedTech.id}`,{method:"PATCH",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({home_address:addr})});
                  onRefresh&&onRefresh();
                }
              }
            }}/>
          {selectedTech.home_lat&&selectedTech.home_lng&&(
            <div style={{fontSize:11,color:"#065F46"}}>✓ Geocoded: {Number(selectedTech.home_lat).toFixed(4)}, {Number(selectedTech.home_lng).toFixed(4)}</div>
          )}
        </div>

        {/* Job Completion Settings */}
        <div className="card" style={{marginBottom:16}}>
          <div className="card-title" style={{marginBottom:12}}>⚙️ Job Completion Mode</div>
          <div style={{display:"flex",flexDirection:isMobile?"column":"row",gap:8,marginBottom:12}}>
            {[{v:"manual",label:"🖐 Manual",sub:"Tap Start & Complete on each job"},{v:"auto",label:"📍 Auto GPS",sub:"Detects arrival & departure automatically"}].map(opt=>(
              <div key={opt.v} role="button" tabIndex={0}
                onClick={async()=>{
                  await fetch(`${SUPABASE_URL}/rest/v1/technicians?id=eq.${selectedTech.id}`,{method:"PATCH",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({job_completion_mode:opt.v})});
                  onRefresh&&onRefresh();
                  showToast(`Job completion set to ${opt.label}`);
                }}
                style={{flex:1,minWidth:0,padding:"12px 14px",borderRadius:8,border:`2px solid ${selectedTech.job_completion_mode===opt.v?NAVY:"#e3e8ef"}`,background:selectedTech.job_completion_mode===opt.v?"#f0f4ff":"white",cursor:"pointer"}}>
                <div style={{fontWeight:700,fontSize:13,color:selectedTech.job_completion_mode===opt.v?NAVY:"#42526e"}}>{opt.label}</div>
                <div style={{fontSize:11,color:"#8993a4",marginTop:2}}>{opt.sub}</div>
              </div>
            ))}
          </div>

          {/* Default visit note */}
          <div style={{marginBottom:12}}>
            <div style={{fontSize:12,fontWeight:600,color:"#42526e",marginBottom:6}}>Default Visit Note <span style={{fontWeight:400,color:"#8993a4"}}>(auto-mode only)</span></div>
            <input type="text" placeholder="e.g. Service completed successfully"
              defaultValue={selectedTech.default_visit_note||""}
              style={{width:"100%",border:"1px solid #e3e8ef",borderRadius:6,padding:"8px 10px",fontSize:13}}
              onBlur={async e=>{
                await fetch(`${SUPABASE_URL}/rest/v1/technicians?id=eq.${selectedTech.id}`,{method:"PATCH",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({default_visit_note:e.target.value||null})});
              }}/>
          </div>

          {/* Email notifications */}
          {/* The whole row is the switch: a real <button role="switch"> with a 44px-tall hit area
              (the bare 44x24 track was a 24px target on a phone). */}
          <button type="button" role="switch" aria-checked={selectedTech.email_notifications!==false}
            onClick={async()=>{
              // null/undefined renders as ON (the `!==false` checks), so toggling must go OFF from
              // there — `!null` wrote true and the first tap on a fresh tech did nothing.
              const newVal = selectedTech.email_notifications === false;
              await fetch(`${SUPABASE_URL}/rest/v1/technicians?id=eq.${selectedTech.id}`,{method:"PATCH",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({email_notifications:newVal})});
              onRefresh&&onRefresh();
            }}
            style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,width:"100%",minHeight:isMobile?44:undefined,padding:isMobile?"6px 0":0,margin:0,border:"none",background:"transparent",font:"inherit",textAlign:"left",cursor:"pointer"}}>
            <div style={{minWidth:0}}>
              <div style={{fontSize:13,fontWeight:600,color:"#42526e"}}>Email Notifications</div>
              <div style={{fontSize:11,color:"#8993a4"}}>Receive email when jobs auto-start or complete</div>
            </div>
            <span aria-hidden="true" style={{width:44,height:24,flexShrink:0,borderRadius:12,background:selectedTech.email_notifications!==false?"#22C55E":"#e3e8ef",position:"relative",transition:"background .2s",display:"inline-block"}}>
              <span style={{width:20,height:20,borderRadius:"50%",background:"white",position:"absolute",top:2,left:selectedTech.email_notifications!==false?22:2,transition:"left .2s"}}/>
            </span>
          </button>
        </div>

        {/* Today's Jobs */}
        <TechJobList tech={selectedTech} onRefresh={onRefresh} showToast={showToast}/>

        {/* Stats — computed once for all sections */}
        {(()=>{
          const techTrips    = dbTrips?.filter((t)=>t.technician_id===selectedTech.id)||[];
          const now          = new Date();
          const monthTrips   = techTrips.filter((t)=>{ const d=new Date(t.start_time); return d.getMonth()===now.getMonth()&&d.getFullYear()===now.getFullYear(); });
          const bizTrips     = monthTrips.filter((t)=>t.trip_type==="business");
          const totalMiles   = monthTrips.reduce((s,t)=>s+Number(t.distance_miles||0),0);
          const totalDrivMin = monthTrips.reduce((s,t)=>s+Math.round((new Date(t.end_time).getTime()-new Date(t.start_time).getTime())/60000),0);
          const avgAtClient  = bizTrips.filter((t)=>t.time_at_location_mins>0).length
            ? bizTrips.filter((t)=>t.time_at_location_mins>0).reduce((s,t,_,arr)=>s+t.time_at_location_mins/arr.length,0) : 0;
          const reimb        = bizTrips.reduce((s,t)=>s+Number(t.reimbursement_amount||0),0);

          // Route density — group business trips by calendar day
          const byDay = {};
          const sortedBiz = [...bizTrips].sort((a,b)=>new Date(a.start_time).getTime()-new Date(b.start_time).getTime());
          sortedBiz.forEach((t,i)=>{
            const day = new Date(t.start_time).toLocaleDateString("en-US",{weekday:"long"});
            if (!byDay[day]) byDay[day]={clients:0,driveMin:0};
            byDay[day].clients++;
            // Drive time = trip duration (time between visits)
            const dur = Math.round((new Date(t.end_time).getTime()-new Date(t.start_time).getTime())/60000);
            byDay[day].driveMin += dur;
          });
          const dayScores = Object.entries(byDay).map(([day,d])=>({
            day, clients:d.clients, driveMin:d.driveMin,
            density: d.driveMin>0 ? (d.clients/(d.driveMin/60)) : 0
          }));
          const bestDay      = dayScores.sort((a,b)=>b.density-a.density)[0];
          const avgDensity   = dayScores.length ? dayScores.reduce((s,d)=>s+d.density,0)/dayScores.length : 0;
          const avgClientsDay= dayScores.length ? dayScores.reduce((s,d)=>s+d.clients,0)/dayScores.length : 0;

          const fmtDrive = (m:any) => m>=60?`${Math.floor(m/60)}h ${m%60}m`:`${m}m`;

          return (
            <>
              {/* Section 1 — Service Performance */}
              <div style={{marginBottom:8}}>
                <div style={{fontSize:11,fontWeight:700,color:"#8993a4",letterSpacing:1,textTransform:"uppercase",marginBottom:10}}>Service Performance</div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12,marginBottom:16}}>
                  {[
                    {label:"Clients Assigned", value:stats.assigned,               sub:"active clients",          icon:"👥"},
                    {label:"Monthly Revenue",  value:`$${stats.revenue.toFixed(2)}`,sub:"from assigned clients",   icon:"💰"},
                    {label:"Jobs Completed",   value:stats.completed,               sub:`of ${stats.total} total`, icon:"✓"},
                    {label:"Completion Rate",  value:`${stats.compRate}%`,          sub:`${stats.skipped} skipped`,icon:"📊"},
                  ].map(s=>(
                    <div key={s.label} className="stat-card">
                      <div style={{fontSize:18,marginBottom:6}}>{s.icon}</div>
                      <div className="stat-label">{s.label}</div>
                      <div className="stat-value">{s.value}</div>
                      <div className="stat-sub">{s.sub}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Section 2 — This Month's Driving */}
              <div style={{marginBottom:8}}>
                <div style={{fontSize:11,fontWeight:700,color:"#8993a4",letterSpacing:1,textTransform:"uppercase",marginBottom:10}}>This Month's Driving</div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12,marginBottom:16}}>
                  {[
                    {label:"Miles Driven",       value:`${totalMiles.toFixed(1)} mi`,           sub:"total this month",         icon:"🛣️"},
                    {label:"Total Drive Time",    value:fmtDrive(totalDrivMin),                  sub:"behind the wheel",          icon:"⏱️"},
                    {label:"Avg at Client",       value:avgAtClient>0?`${Math.round(avgAtClient)}m`:"—", sub:"per business visit",icon:"🏠"},
                    {label:"Mileage Reimb.",      value:`$${reimb.toFixed(2)}`,                  sub:"@ IRS rate",                icon:"💵"},
                  ].map(s=>(
                    <div key={s.label} className="stat-card">
                      <div style={{fontSize:18,marginBottom:6}}>{s.icon}</div>
                      <div className="stat-label">{s.label}</div>
                      <div className="stat-value">{s.value}</div>
                      <div className="stat-sub">{s.sub}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Section 3 — Route Efficiency */}
              {dayScores.length>0&&(
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:11,fontWeight:700,color:"#8993a4",letterSpacing:1,textTransform:"uppercase",marginBottom:10}}>Route Efficiency</div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12}}>
                    <div className="stat-card">
                      <div style={{fontSize:18,marginBottom:6}}>📍</div>
                      <div className="stat-label">Avg Route Density</div>
                      <div className="stat-value">{avgDensity>0?avgDensity.toFixed(1):"—"}</div>
                      <div className="stat-sub">clients per hour driven</div>
                    </div>
                    <div className="stat-card">
                      <div style={{fontSize:18,marginBottom:6}}>🏆</div>
                      <div className="stat-label">Most Efficient Day</div>
                      <div className="stat-value">{bestDay?bestDay.day:"—"}</div>
                      <div className="stat-sub">{bestDay?`${bestDay.clients} clients · ${fmtDrive(bestDay.driveMin)} drive`:"no data yet"}</div>
                    </div>
                    <div className="stat-card">
                      <div style={{fontSize:18,marginBottom:6}}>📅</div>
                      <div className="stat-label">Avg Clients / Day</div>
                      <div className="stat-value">{avgClientsDay>0?avgClientsDay.toFixed(1):"—"}</div>
                      <div className="stat-sub">on working days this month</div>
                    </div>
                  </div>
                </div>
              )}
            </>
          );
        })()}

        {/* Assigned Clients */}
        <div className="card" style={{marginBottom:16}}>
          <div className="card-title">Assigned Clients ({techClients.length})</div>
          {techClients.length===0&&<div className="empty"><p>No clients assigned</p></div>}
          <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
            {techClients.map(c=>(
              <div key={c.id} style={{background:"#f5f6f8",borderRadius:6,padding:"6px 12px",fontSize:12}}>
                <span style={{fontWeight:600,color:NAVY}}>{c.name}</span>
                <span style={{color:"#8993a4",marginLeft:6}}>{c.frequency||"No plan"} · {c.dogs||"?"} dog{c.dogs!==1?"s":""}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Clean Times — GPS auto-tracked per-client averages + individual visits */}
        <div className="card" style={{marginBottom:16}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,marginBottom:10,flexWrap:"wrap"}}>
            <div className="card-title" style={{margin:0}}>🧹 Clean Times <span style={{fontWeight:400,color:"#8993a4",fontSize:12}}>· GPS auto-tracked</span></div>
            {gps.overallAvgMin!=null&&<span className="badge badge-blue">Avg ~{gps.overallAvgMin} min · {gps.totalVisits} visit{gps.totalVisits>1?"s":""}</span>}
          </div>
          {gps.perClient.length===0
            ? <div className="empty"><p>No GPS-tracked visits linked to this tech yet.</p></div>
            : (<>
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Client</th><th>Avg clean</th><th>Visits</th><th>Last visit</th></tr></thead>
                    <tbody>
                      {(showAllClients?gps.perClient:gps.perClient.slice(0,LIST_PAGE)).map(c=>(
                        <tr key={c.client_id}>
                          <td style={{fontWeight:600,fontSize:13}}>{c.name}</td>
                          <td style={{fontSize:13}}>~{c.avgMin} min</td>
                          <td style={{fontSize:12,color:"#42526e"}}>{c.visits}</td>
                          <td style={{fontSize:12,color:"#8993a4"}}>{(c.lastVisit||"").slice(0,10)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:10}}>
                  {gps.perClient.length>LIST_PAGE&&(
                    <button className="btn btn-ghost btn-sm" onClick={()=>setShowAllClients(v=>!v)}>
                      {showAllClients?`Show top ${LIST_PAGE}`:`Show all ${gps.perClient.length} clients →`}
                    </button>
                  )}
                  <button className="btn btn-ghost btn-sm" onClick={()=>setShowAllVisits(v=>!v)}>
                    {showAllVisits?"Hide individual visits":`View all ${gps.totalVisits} visits →`}
                  </button>
                </div>
                {showAllVisits&&(
                  <div className="table-wrap" style={{marginTop:10}}>
                    <table>
                      <thead><tr><th>Date</th><th>Client</th><th>On site</th></tr></thead>
                      <tbody>
                        {gps.allVisits.map((v,i)=>(
                          <tr key={i}>
                            <td style={{fontSize:12}}>{(v.created_at||"").slice(0,10)}</td>
                            <td style={{fontWeight:600,fontSize:13}}>{v.name}</td>
                            <td style={{fontSize:12,color:"#42526e"}}>{v.minutes} min</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>)}
        </div>

        {/* Recent visits */}
        <div className="card">
          <div className="card-title">Recent Visit History</div>
          {loading&&<div style={{padding:20,textAlign:"center",color:"#8993a4"}}>Loading...</div>}
          {!loading&&techVisits.length===0&&<div className="empty"><p>No visit records yet</p></div>}
          {!loading&&techVisits.length>0&&isMobile&&(
            <div>
              {/* Phone: one stacked row per visit (the 5-column table needed a sideways scroll
                  that hid Status/Duration). Same data, same paging control. */}
              {(showAllHistory?techVisits:techVisits.slice(0,LIST_PAGE)).map((v,i)=>{
                const dur = v.started_at&&v.completed_at ? Math.floor((new Date(v.completed_at)-new Date(v.started_at))/60000)+"m" : "—";
                const cl  = clients.find(c=>c.id===v.client_id);
                return (
                  <div key={v.id||i} style={{padding:"10px 0",borderTop:"1px solid var(--border)"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",gap:10}}>
                      <div style={{fontWeight:600,fontSize:14,color:"var(--text)",minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{cl?.name||"Unknown"}</div>
                      <div style={{fontSize:12,color:"var(--text2)",flexShrink:0}}>{v.date}</div>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginTop:5,flexWrap:"wrap",fontSize:12,color:"var(--text2)"}}>
                      <span>{v.service_type==="deo"?"💨 Deo":"🧹 Cleanup"}</span>
                      <span className={`badge ${v.status==="completed"?"badge-green":v.status==="skipped"?"badge-gold":"badge-red"}`} style={{textTransform:"capitalize",fontSize:10}}>{v.status}</span>
                      <span>⏱ {dur}</span>
                    </div>
                  </div>
                );
              })}
              {techVisits.length>LIST_PAGE&&(
                <button className="btn btn-ghost btn-sm" style={{marginTop:10}} onClick={()=>setShowAllHistory(v=>!v)}>
                  {showAllHistory?`Show latest ${LIST_PAGE}`:`Show all ${techVisits.length} visits →`}
                </button>
              )}
            </div>
          )}
          {!loading&&techVisits.length>0&&!isMobile&&(
            <div className="table-wrap">
              <table>
                <thead><tr><th>Date</th><th>Client</th><th>Service</th><th>Status</th><th>Duration</th></tr></thead>
                <tbody>
                  {(showAllHistory?techVisits:techVisits.slice(0,LIST_PAGE)).map((v,i)=>{
                    const dur = v.started_at&&v.completed_at ? Math.floor((new Date(v.completed_at)-new Date(v.started_at))/60000)+"m" : "—";
                    const cl  = clients.find(c=>c.id===v.client_id);
                    return (
                      <tr key={v.id||i}>
                        <td style={{fontSize:12}}>{v.date}</td>
                        <td style={{fontWeight:600,fontSize:13}}>{cl?.name||"Unknown"}</td>
                        <td>{v.service_type==="deo"?"💨 Deo":"🧹 Cleanup"}</td>
                        <td><span className={`badge ${v.status==="completed"?"badge-green":v.status==="skipped"?"badge-gold":"badge-red"}`} style={{textTransform:"capitalize",fontSize:10}}>{v.status}</span></td>
                        <td style={{fontSize:12,color:"#42526e"}}>{dur}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {techVisits.length>LIST_PAGE&&(
                <button className="btn btn-ghost btn-sm" style={{marginTop:10}} onClick={()=>setShowAllHistory(v=>!v)}>
                  {showAllHistory?`Show latest ${LIST_PAGE}`:`Show all ${techVisits.length} visits →`}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Tech list view
  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <h2 style={{fontFamily:"Syne",fontWeight:800,fontSize:20,color:NAVY,margin:0}}>Technicians</h2>
      </div>
      {technicians.length===0&&<div className="empty"><p>No technicians yet. Add them in Settings.</p></div>}
      <div className="grid-2">
        {technicians.map(tech=>{
          const stats = getTechStats(tech);
          return (
            <div key={tech.id} className="card" style={{cursor:"pointer",transition:"box-shadow .15s,background .15s"}}
              onClick={()=>setSelectedTech(tech)}
              onMouseEnter={e=>e.currentTarget.style.boxShadow="0 4px 16px rgba(0,0,0,.1)"}
              onMouseLeave={e=>e.currentTarget.style.boxShadow=""}
              onTouchStart={e=>{ e.currentTarget.style.background="var(--bg)"; }}
              onTouchEnd={e=>{ e.currentTarget.style.background=""; }}
              onTouchCancel={e=>{ e.currentTarget.style.background=""; }}>
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
                <div style={{width:44,height:44,borderRadius:"50%",background:tech.active?NAVY:"#8993a4",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,color:"white",fontWeight:700,flexShrink:0}}>
                  {tech.name.charAt(0)}
                </div>
                <div style={{minWidth:0,flex:"1 1 0"}}>
                  <div style={{fontWeight:700,fontSize:15,color:"#1a1f36"}}>{tech.name}</div>
                  <div style={{fontSize:11,color:"#8993a4",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{tech.email||"No email"}</div>
                </div>
                <span className={`badge ${tech.active?"badge-green":"badge-gray"}`} style={{marginLeft:"auto",flexShrink:0}}>{tech.active?"Active":"Inactive"}</span>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
                {[
                  {label:"Clients",    val:stats.assigned},
                  {label:"Completion", val:`${stats.compRate}%`},
                  {label:"Revenue",    val:`$${stats.revenue.toFixed(0)}/mo`},
                ].map(s=>(
                  <div key={s.label} style={{background:"#f5f6f8",borderRadius:6,padding:"8px 10px",textAlign:"center"}}>
                    <div style={{fontWeight:800,fontSize:16,color:NAVY}}>{s.val}</div>
                    <div style={{fontSize:10,color:"#8993a4",marginTop:2}}>{s.label}</div>
                  </div>
                ))}
              </div>
              <div style={{marginTop:10,fontSize:11,color:"#8993a4",textAlign:"right"}}>{isMobile?"Tap to view full profile →":"Click to view full profile →"}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
